<template>
  <div class="h-full flex flex-col bg-[#f8fafc] gap-6 overflow-auto">
    <DepartmentModal
      ref="departmentModalRef"
      :isOpen="showModal"
      :editDepartment="editingDepartment"
      :branches="branches"
      @close="closeModal"
      @save="createDepartment"
      @update="updateDepartment"
    />

    <SubDepartmentModal
      v-if="selectedDepartmentForSub"
      :is-open="showSubDeptModal"
      :department="selectedDepartmentForSub"
      @close="closeSubDeptModal"
    />

    <!-- Table -->
    <div class="w-full overflow-x-auto overflow-y-auto max-h-96">
      <table class="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
        <thead>
          <tr>
            <th class="w-12 text-center">No.</th>
            <th class="w-16 text-center">Image</th>
            <th @click="sortBy('name')" class="sort-col">
              Name <span v-if="sortKey === 'name'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('printerIP')" class="sort-col">
              Printer IP <span v-if="sortKey === 'printerIP'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('printer_status')" class="sort-col">
              Printer Status <span v-if="sortKey === 'printer_status'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th class="text-center"></th>
            <!-- <th>Branch</th> -->
            <th @click="sortBy('created_at')" class="sort-col">
              Created At <span v-if="sortKey === 'created_at'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th class="text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="!loading">
            <tr v-for="(dep, index) in sortedDepartments" :key="dep.id" class="hover:bg-slate-100/50 transition-colors group">
            <td class="text-center font-medium pr-1 text-slate-400">{{ index + 1 }}</td>
            <td class="text-center px-1">
              <div class="inline-flex">
                <img v-if="dep.image" :src="resolveCompanyLogoUrl(dep.image)" class="w-7 h-6 object-contain">
                <div v-else class="w-7 h-6 rounded-md bg-slate-100 flex items-center justify-center text-[10px] text-slate-400 font-bold uppercase shrink-0">
                  {{ dep.name.charAt(0) }}
                </div>
              </div>
            </td>
            <td class="font-semibold text-slate-700"><a class="text-blue-700 hover:text-blue-700 underline cursor-pointer"     @click="openSubDeptModal(dep)">{{ dep.name }}</a></td>
            <td class="font-mono text-[11px] text-slate-500">{{ dep.printerIP || '-' }}</td>
            <td>
              <span class="px-2 py-0.5 rounded-full text-[10px] font-bold" :class="dep.printer_status === 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'">
                {{ dep.printer_status === 1 ? 'ON' : 'OFF' }}
              </span>
            </td>
            <td class="text-center">
              <input
                type="checkbox"
                class="w-4 h-4 accent-[#0d4169] cursor-default pointer-events-none"
                :checked="!!(dep.isCentral ?? dep.is_central)"
                disabled
              />
            </td>
            <!-- <td>{{ dep.branch?.name || '-' }}</td> -->
            <td>{{ formatDate(dep.created_at) }}</td>
            <td>
  <div class="flex items-center gap-2 whitespace-nowrap">
    <button
      type="button"
      @click="editDepartment(dep)"
      class="editbtn text-emerald-600"
      :class="canEditDepartment ? 'hover:underline' : 'opacity-50 cursor-not-allowed action-disabled-marker'"
      :disabled="!canEditDepartment"
    >
      Edit
    </button>

    <label
      class="toggle-switch"
      :class="[
        dep.__togglingStatus ? 'opacity-50 pointer-events-none' : '',
        !canEditDepartment ? 'opacity-50 cursor-not-allowed action-disabled-marker' : ''
      ]"
      title="Toggle status"
    >
      <input
        type="checkbox"
        :checked="(dep.status ?? 1) === 1"
        :disabled="!canEditDepartment"
        @change="toggleDepartmentStatus(dep)"
      >
      <span class="slider"></span>
    </label>

    <!-- <button
      type="button"
      @click="openSubDeptModal(dep)"
      class="p-1 text-emerald-600 hover:text-emerald-700 hover:underline transition-all"
      title="View Sub-Departments"
    >
      <span class="text-[11px] font-bold">Sub Department</span>
    </button> -->
  </div>
</td>
            </tr>
          </template>
          <tr v-if="loading">
  <td colspan="8" class="text-center py-10">
    <div class="flex items-center justify-center">
      <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
    </div>
  </td>
</tr>
          <tr v-else-if="error">
            <td colspan="8" class="text-center py-10 text-red-500">{{ error }}</td>
          </tr>
          <tr v-else-if="departments.length === 0">
            <td colspan="8" style="text-align:center; color:#94a3b8; padding: 48px 15px; font-style: italic;">
              No departments found for this company.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import store from '../../store'
import { apiFetch, WS_BASE_URL, normalizeUploadValue, resolveCompanyLogoUrl } from '../../utils/api'
import DepartmentModal from './DepartmentModal.vue'
import SubDepartmentModal from './SubDepartmentModal.vue'

const departments = ref([])
const branches = ref([])
const loading = ref(true)
const error = ref('')
const departmentModalRef = ref(null)
const sortKey = ref('name')
const sortOrder = ref('asc')

const showModal = ref(false)
const editingDepartment = ref(null)

const showSubDeptModal = ref(false)
const selectedDepartmentForSub = ref(null)

let deptSyncSocket = null
let deptSyncReconnectTimer = null

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)
const userId = computed(() => currentUser.value?.id || null)
const userPrivileges = computed(() => {
  if (!currentUser.value?.privilege) return {}
  return typeof currentUser.value.privilege === 'string'
    ? JSON.parse(currentUser.value.privilege)
    : currentUser.value.privilege
})
const hasAdminPrivilege = computed(() => userPrivileges.value?.All === true || userPrivileges.value?.all === true)
const departmentPrivilege = computed(() => userPrivileges.value?.Department || userPrivileges.value?.department || null)
const canAddDepartment = computed(() => hasAdminPrivilege.value || !!departmentPrivilege.value?.Add)
const canEditDepartment = computed(() => hasAdminPrivilege.value || !!departmentPrivilege.value?.Edit)

const sortedDepartments = computed(() => {
  const result = [...departments.value]
  result.sort((a, b) => {
    const modifier = sortOrder.value === 'asc' ? 1 : -1
    const valA = (a[sortKey.value] ?? '').toString().toLowerCase()
    const valB = (b[sortKey.value] ?? '').toString().toLowerCase()
    if (valA < valB) return -1 * modifier
    if (valA > valB) return 1 * modifier
    return 0
  })
  return result
})

function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

function formatDate(value) {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'
  return d.toISOString().slice(0, 10)
}

async function loadBranches() {
  if (!companyId.value) return
  const response = await apiFetch(`/branches/company/${companyId.value}`, { method: 'GET' })
  if (!response.ok) return
  const data = await response.json().catch(() => [])
  branches.value = Array.isArray(data) ? data : []
  branches.value.sort((a, b) => a.name.localeCompare(b.name))
}

async function loadDepartments() {
  loading.value = true
  error.value = ''
  if (!companyId.value) {
    departments.value = []
    error.value = 'Company ID not found in user profile.'
    loading.value = false
    return
  }

  try {
    let endpoint = `/departments/company/${companyId.value}`
    if (branchId.value) {
      endpoint = `/departments/branch/${branchId.value}`
    }
    
    const response = await apiFetch(endpoint, { method: 'GET' })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to load departments')
    }
    const data = await response.json()
    departments.value = Array.isArray(data) ? data : []
  } catch (err) {
    error.value = err.message || 'Failed to load departments'
  } finally {
    loading.value = false
  }
}

function openAddModal() {
  if (!canAddDepartment.value) return
  showModal.value = true
}

function closeModal() {
  showModal.value = false
  editingDepartment.value = null
}

function editDepartment(dep) {
  if (!canEditDepartment.value) return
  editingDepartment.value = dep
  showModal.value = true
}

function openSubDeptModal(dep) {
  selectedDepartmentForSub.value = dep
  showSubDeptModal.value = true
}

function closeSubDeptModal() {
  showSubDeptModal.value = false
  selectedDepartmentForSub.value = null
}

async function createDepartment(payload) {
  const response = await apiFetch('/departments', {
    method: 'POST',
    body: JSON.stringify({
      companyId: branchId.value ? null : companyId.value,
      branchId: branchId.value || null,
      name: payload.name,
      remark: payload.remark,
      printerIP: payload.printerIP,
      isCentral: !!payload.isCentral,
      printer_status: payload.printer_status,
      image: normalizeUploadValue(payload.image),
      created_by: userId.value,
    }),
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    const msg = data.message || 'Failed to create department'
    departmentModalRef.value?.setError(msg)
    return
  }

  closeModal()
  await loadDepartments()
}

async function updateDepartment(payload) {
  const response = await apiFetch(`/departments/${payload.id}`, {
    method: 'PUT',
    body: JSON.stringify({
      name: payload.name,
      remark: payload.remark,
      printerIP: payload.printerIP,
      how_many_copies: payload.howManyCopies,
      printer_status: payload.printer_status,
      image: normalizeUploadValue(payload.image),
      updated_by: userId.value,
    }),
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    const msg = data.message || 'Failed to update department'
    departmentModalRef.value?.setError(msg)
    return
  }

  closeModal()
  await loadDepartments()
}

async function toggleDepartmentStatus(dep) {
  if (!canEditDepartment.value) return
  if (!dep?.id || dep.__togglingStatus) return
  dep.__togglingStatus = true
  const current = (dep.status ?? 1) === 1 ? 1 : 0
  const next = current === 1 ? 0 : 1
  try {
    const res = await apiFetch(`/departments/${dep.id}/toggle-status`, {
      method: 'PATCH',
      body: JSON.stringify({ status: next })
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to toggle department status')
    }
    dep.status = next
  } catch (e) {
    alert(e.message || 'Failed to toggle department status')
  } finally {
    dep.__togglingStatus = false
  }
}

function onOpenDepartmentModal() {
  if (!canAddDepartment.value) return
  openAddModal()
}

function disconnectDeptSyncRealtime() {
  if (deptSyncReconnectTimer) {
    clearTimeout(deptSyncReconnectTimer)
    deptSyncReconnectTimer = null
  }
  if (deptSyncSocket) {
    try {
      deptSyncSocket.close()
    } catch (_) {
      // ignore close failures
    }
    deptSyncSocket = null
  }
}

function connectDeptSyncRealtime() {
  disconnectDeptSyncRealtime()

  const user = currentUser.value
  if (!user || typeof WebSocket === 'undefined') return

  const scopedCompanyId = user.companyId || user.company_id || user.company?.id
  if (!scopedCompanyId) return

  const scopedBranchId = user.branchId || user.branch_id || user.branch?.id || null
  const socket = new WebSocket(WS_BASE_URL)
  deptSyncSocket = socket

  socket.addEventListener('open', () => {
    socket.send(JSON.stringify({
      type: 'frontend.subscribe',
      company_id: scopedCompanyId,
      branch_id: scopedBranchId,
    }))
  })

  socket.addEventListener('message', (event) => {
    try {
      const message = JSON.parse(event.data)
      if (message.event?.startsWith('department.') || message.event?.startsWith('sub_department.')) {
        loadDepartments()
      }
    } catch (_) {
      // ignore malformed realtime payloads
    }
  })

  socket.addEventListener('close', () => {
    deptSyncSocket = null
    deptSyncReconnectTimer = setTimeout(() => {
      connectDeptSyncRealtime()
    }, 3000)
  })
}

onMounted(async () => {
  await loadBranches()
  await loadDepartments()
  connectDeptSyncRealtime()
  window.addEventListener('open-department-modal', onOpenDepartmentModal)
})

onUnmounted(() => {
  disconnectDeptSyncRealtime()
  window.removeEventListener('open-department-modal', onOpenDepartmentModal)
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  color: #1e293b;
}

th,
td {
  padding: 5px 6px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.8rem;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

th.sort-col {
  cursor: pointer;
}
th.sort-col:hover {
  background: #e2e8f0;
}

tbody tr:hover {
  background: #f1f5f9;
}

.editbtn {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

/* Toggle Switch (same style as usersPage.vue) */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 15px;
  cursor: pointer;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 34px;
  transition: 0.2s;
}
.slider::before {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  left: 2px;
  top: 2.5px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}
.toggle-switch input:checked + .slider {
  background: #22c55e;
}
.toggle-switch input:not(:checked) + .slider {
  background: #ef4444;
}
.toggle-switch input:checked + .slider::before {
  transform: translateX(13px);
}

.action-disabled-marker {
  position: relative;
}

.action-disabled-marker:hover::after {
  content: '⊘';
  position: absolute;
  top: -8px;
  right: -10px;
  font-size: 12px;
  font-weight: 700;
  color: #dc2626;
  line-height: 1;
  pointer-events: none;
}
</style>

